// Bottom Navigation Component
// TikTok-style mobile bottom navigation for Plenty of Hearts in full effect

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp
} from "lucide-react";
import {
  BottomNavigationContainer,
  NavigationItem,
  NavigationIcon,
  NavigationLabel,
  SpecialActionButton,
  SwipeIndicator,
} from "../../../styles/SocialFeed/BottomNavigation.styles";
import { useAuthContext } from "../../../providers";
import { Icon } from "../../shared";
import { navigationItems, getActiveNavigationItem, NavigationItemConfig } from "../navigationItems";

interface BottomNavigationProps {
  isVisible?: boolean;
  onSwipeModeToggle?: (enabled: boolean) => void;
}

export const BottomNavigation: React.FC<BottomNavigationProps> = ({
  isVisible = true,
  onSwipeModeToggle,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | 'up' | null>(null);

  const activeItem = getActiveNavigationItem(location.pathname);

  // Handle navigation item click
  const handleNavigationClick = (item: NavigationItemConfig) => {
    if (item.isSpecial) {
      // Handle special swipe action
      handleSwipeAction();
    } else {
      navigate(item.route);
    }
  };

  // Handle swipe action - Open modal instead of navigating
  const handleSwipeAction = () => {
    // Always open the swipe modal for immersive experience
    onSwipeModeToggle?.(true);
    setSwipeDirection('up');
    setTimeout(() => setSwipeDirection(null), 1000);
  };


  // Hide navigation on scroll (optional)
  useEffect(() => {
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // You can add logic here to hide/show navigation based on scroll
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Swipe Direction Indicator */}
      <AnimatePresence>
        {swipeDirection && (
          <SwipeIndicator
            $direction={swipeDirection}
            className="visible"
            as={motion.div}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            {swipeDirection === 'left' && (
              <>
                <Icon icon={ArrowLeft} size={16} /> Pass
              </>
            )}
            {swipeDirection === 'right' && (
              <>
                Like <Icon icon={ArrowRight} size={16} />
              </>
            )}
            {swipeDirection === 'up' && (
              <>
                <Icon icon={ArrowUp} size={16} /> Swipe Mode
              </>
            )}
          </SwipeIndicator>
        )}
      </AnimatePresence>


      {/* Bottom Navigation */}
      <BottomNavigationContainer
        $isVisible={isVisible}
        as={motion.nav}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      >
        {navigationItems.map((item) => (
          <NavigationItem
            key={item.id}
            $isActive={activeItem === item.id}
            $hasNotification={item.hasNotification}
            $variant={item.variant}
            onClick={() => handleNavigationClick(item)}
            as={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            {item.isSpecial ? (
              <SpecialActionButton
                $isActive={activeItem === item.id}
              >
                {activeItem === item.id ? item.activeIcon : item.icon}
              </SpecialActionButton>
            ) : (
              <>
                <NavigationIcon $isActive={activeItem === item.id}>
                  {activeItem === item.id ? (item.activeIcon || item.icon) : item.icon}
                </NavigationIcon>
                <NavigationLabel $isActive={activeItem === item.id}>
                  {item.label}
                </NavigationLabel>
              </>
            )}
          </NavigationItem>
        ))}
      </BottomNavigationContainer>
    </>
  );
};
